"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import SoundCard from "@/components/ui/sound-card"
import { StaticSoundRow } from "@/components/ui/sound-card/static-row"
import { ErrorBoundary } from "@/components/error-boundary"
import { ViewSwitch } from "@/components/ui/tls/view-switch"
import type { Sound } from "@/lib/supabase"

type SortKey = "popular" | "newest" | "alpha" | "shortest"

// Windowed hydration (mirrors app/sounds/sound-grid.tsx, TES-1077): only the
// first EAGER_CARD_RENDER cards mount on first paint; every slot past it
// renders the static row until it scrolls near the viewport. Collection
// pages can carry 200 sounds, and mounting every SoundCard (each runs the full
// audio/favorites/tracking hook stack) on one post-load task was the dominant
// mobile TBT cost that regressed this page-type (TES-834).
//
// The deferred slot is NOT an empty band. Collections are sitemapped hubs, and
// an empty placeholder cost the server HTML every sound link past the sixth
// (164 → 6 on gaming-nostalgia). StaticSoundRow carries the link, title and
// meta of every row from the first byte; only the hook stack is deferred.
const EAGER_CARD_RENDER = 6
const HYDRATION_ROOT_MARGIN = "400px 0px"

interface SortGridProps {
  sounds: Sound[]
  collectionLabel: string
  accentToken: string
}

function CardSlot({
  sound,
  eager,
  accentToken,
}: {
  sound: Sound
  eager: boolean
  accentToken: string
}) {
  // Keep the server and hydration pass identical. The deferred browser fallback
  // below schedules an upgrade where IntersectionObserver is unavailable.
  const [hydrated, setHydrated] = useState(eager)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (hydrated) return
    if (typeof IntersectionObserver === "undefined") {
      const fallback = window.setTimeout(() => setHydrated(true), 0)
      return () => window.clearTimeout(fallback)
    }
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHydrated(true)
          observer.disconnect()
        }
      },
      { rootMargin: HYDRATION_ROOT_MARGIN }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hydrated])

  return (
    <div ref={ref}>
      {hydrated ? (
        <ErrorBoundary>
          <SoundCard
            sound={sound}
            showCategory={true}
            accentToken={accentToken}
            disableEntryAnimation
            deferAudioUntilVisible
          />
        </ErrorBoundary>
      ) : (
        <StaticSoundRow sound={sound} showCategory={true} />
      )}
    </div>
  )
}

const SORT_OPTIONS = [
  { id: "popular", label: "Popular" },
  { id: "newest", label: "Newest" },
  { id: "alpha", label: "A–Z" },
  { id: "shortest", label: "Shortest" },
] as const satisfies readonly { id: SortKey; label: string }[]

function compareBy(sounds: Sound[], sortKey: SortKey): Sound[] {
  const arr = [...sounds]
  switch (sortKey) {
    case "popular":
      return arr.sort((a, b) => (b.downloads ?? 0) - (a.downloads ?? 0))
    case "newest":
      return arr.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0
        return tb - ta
      })
    case "alpha":
      return arr.sort((a, b) => a.title.localeCompare(b.title))
    case "shortest":
      return arr.sort((a, b) => (a.duration ?? 0) - (b.duration ?? 0))
  }
}

export default function CollectionSortGrid({
  sounds,
  collectionLabel,
  accentToken,
}: SortGridProps) {
  const [sortKey, setSortKey] = useState<SortKey>("popular")
  const sorted = useMemo(() => compareBy(sounds, sortKey), [sounds, sortKey])

  return (
    <>
      <div className="mb-8 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
        <h2 className="tls-h2">Sounds in {collectionLabel}</h2>
        <ViewSwitch label="Sort sounds" options={SORT_OPTIONS} value={sortKey} onChange={setSortKey} />
      </div>

      <div className="grid grid-cols-1 gap-0">
        {sorted.map((sound, index) => (
          <CardSlot
            key={sound.id}
            sound={sound}
            eager={index < EAGER_CARD_RENDER}
            accentToken={accentToken}
          />
        ))}
      </div>
    </>
  )
}
